const CSS_VARIABLE_REGEX = /var\(--([^,\s)]+)(?:\s*,\s*([^)]+))?\)/g;

export class VariableResolver {
  static hasVariableReferences(value: string): boolean {
    return CSS_VARIABLE_REGEX.test(value);
  }

  static resolveVariable(
    value: string,
    themeVariables: Record<string, string>,
  ): string {
    if (!value) {
      return '';
    }

    return value
      .trim()
      .replace(CSS_VARIABLE_REGEX, (match, variableName, fallback) => {
        const resolvedThemeValue = themeVariables[variableName];

        if (resolvedThemeValue !== undefined) {
          return resolvedThemeValue;
        }

        if (fallback !== undefined) {
          return fallback.trim();
        }

        return match;
      });
  }

  static resolveVariables(
    value: string,
    themeVariables: Record<string, string>,
  ): string {
    let resolvedValue = value;
    let iterations = 0;
    while (
      VariableResolver.hasVariableReferences(resolvedValue) &&
      iterations < 10
    ) {
      iterations++;
      resolvedValue = VariableResolver.resolveVariable(
        resolvedValue,
        themeVariables,
      );
    }

    return resolvedValue;
  }

  static resolveVariablesInRecord(
    values: Record<string, string>,
    themeVariables: Record<string, string>,
  ): Record<string, string> {
    const resolved: Record<string, string> = {};

    for (const [key, value] of Object.entries(values)) {
      resolved[key] = VariableResolver.resolveVariables(value, themeVariables);
    }

    return resolved;
  }

  static extractVariableNames(value: string): string[] {
    const variables: string[] = [];
    const regex = new RegExp(CSS_VARIABLE_REGEX);
    let match = regex.exec(value);

    while (match !== null) {
      variables.push(match[1]);
      match = regex.exec(value);
    }

    return variables;
  }

  static extractVariableNamesFromRecord(
    values: Record<string, string>,
  ): string[] {
    const variableSet = new Set<string>();

    for (const value of Object.values(values)) {
      const vars = VariableResolver.extractVariableNames(value);
      vars.forEach((v) => variableSet.add(v));
    }

    return Array.from(variableSet);
  }

  /**
   * Extracts variable names from both CSS properties and custom properties,
   * combines them into a unique set, and returns the combined list.
   *
   * @param cssProperties - CSS properties record
   * @param cssCustomProperties - CSS custom properties record
   * @returns Array of unique variable names (without the -- prefix)
   */
  static extractAllVariableNames(
    cssProperties: Record<string, string>,
    cssCustomProperties: Record<string, string>,
  ): string[] {
    const cssPropsVariables =
      VariableResolver.extractVariableNamesFromRecord(cssProperties);
    const customPropsVariables =
      VariableResolver.extractVariableNamesFromRecord(cssCustomProperties);

    return [...new Set([...cssPropsVariables, ...customPropsVariables])];
  }

  /**
   * Extracts variable name from var() syntax.
   * Example: "var(--color-primary)" -> "color-primary"
   */
  static extractVarName(value: string): string {
    const match = value.trim().match(/^var\(([^,]+)(?:,.*)?\)$/);
    if (!match) {
      return value;
    }
    return match[1].trim().replace(/^--/, '');
  }

  /**
   * Checks if a value is a single CSS variable (like 'var(--x)') and not a semantic string.
   * Returns false for values like 'var(--w) solid var(--c)' or '2px solid red'.
   */
  static isSingleCSSVariable(value: string): boolean {
    const trimmed = value.trim();
    // Check if it starts with var(-- and the rest is just the variable (no other content)
    if (!trimmed.startsWith('var(--')) {
      return false;
    }

    // Simple check: if there's only one 'var(' occurrence, it's a single variable
    const varCount = (trimmed.match(/var\(/g) || []).length;
    return varCount === 1;
  }

  /**
   * Recursively resolves a shorthand value until it's not a single CSS variable anymore.
   * Prevents infinite loops by tracking visited variables.
   */
  static resolveVariableRecursively(
    value: string,
    themeVariables: Record<string, string>,
    visited: Set<string> = new Set(),
  ): string {
    // If not a single variable, return as-is
    if (!VariableResolver.isSingleCSSVariable(value)) {
      return value;
    }

    // Resolve the variable
    const resolved = VariableResolver.resolveVariable(value, themeVariables);

    // If resolution didn't change anything, stop (variable not found in theme)
    if (resolved === value) {
      return value;
    }

    // Detect cycles
    if (visited.has(resolved)) {
      console.warn(
        `[resolveShorthandRecursively] Circular reference detected for ${value}`,
      );
      return value;
    }

    visited.add(resolved);

    // If still a single variable, recursively resolve
    if (VariableResolver.isSingleCSSVariable(resolved)) {
      return VariableResolver.resolveVariableRecursively(
        resolved,
        themeVariables,
        visited,
      );
    }

    // Not a single variable anymore - semantic string reached
    return resolved;
  }
}
